"use client";

import { useMemo } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { useStep } from "@/hooks/use-step-handler";
import { RetailClientStep } from "@/components/backoffice/cat/vehicle-assignment/client-step/retail/retail-client-content";
import { RetailVehicleBodyStep } from "@/components/backoffice/cat/vehicle-assignment/vehicle-bodywork-step/vehicle-bodywork-content";
import VehicleIdentification from "@/components/backoffice/cat/vehicle-assignment/vehicle-identification-step/vehicle-identification-content";
import { RetailVehicleTradeInStep } from "@/components/backoffice/cat/vehicle-assignment/vehicle-trade-in-step/vehicle-trade-in-content";
import { getRetailVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-retail-step-definitions";
import type { VehicleAssignmentFlowContext } from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";
import { RetailAdditionalInformationsStep } from "@/components/backoffice/cat/vehicle-assignment/additional-informations-step/additional-informations-step-content";
import { RetailFundingStep } from "@/components/backoffice/cat/vehicle-assignment/funding/funding-step";
import { RetailSaleTypeStep } from "../sale-type-step/retail/retail-sale-type-content";

function VehicleAssignmentRetailContent({
  currentKvps,
  onCanProceedChange,
}: {
  currentKvps?: Kvps | null;
  onCanProceedChange?: (canProceed: boolean) => void;
}) {
  const { step } = useStep();

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({ kvps: currentKvps ?? null }),
    [currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getRetailVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const def = visibleDefinitions[step - 1];

  if (!def) {
    return (
      <Card>
        <CardContent className="p-6 text-gray-600">
          Étape invalide (WIP)
        </CardContent>
      </Card>
    );
  }

  switch (def.id) {
    case "vehicle_identification":
      return (
        <VehicleIdentification
          onCanProceedChange={onCanProceedChange}
          currentKvps={currentKvps}
        />
      );
    case "client":
      return <RetailClientStep onCanProceedChange={onCanProceedChange} />;
    case "vehicle_bodywork":
      return <RetailVehicleBodyStep onCanProceedChange={onCanProceedChange} />;
    case "vehicle_trade-in":
      return (
        <RetailVehicleTradeInStep onCanProceedChange={onCanProceedChange} />
      );
    case "additional_informations":
      return (
        <RetailAdditionalInformationsStep
          onCanProceedChange={onCanProceedChange}
          currentKvps={currentKvps}
        />
      );
    case "leasing_financing":
      return <RetailFundingStep onCanProceedChange={onCanProceedChange} />;
    case "selling_type_retail":
      return <RetailSaleTypeStep />;
    default:
      return (
        <Card>
          <CardContent className="p-6 text-gray-600">
            Contenu étape (WIP) — {def.id}
          </CardContent>
        </Card>
      );
  }
}

export { VehicleAssignmentRetailContent };
